import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  InMemoryCache,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ErrorLink } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { createClient } from 'graphql-ws';

import {
  trackGraphQLRetriesFailed,
  trackGraphQLRetriesStarted,
} from '@libs/feature-toggle';

function apolloClientFactory(): () => ApolloClient {
  let client: ApolloClient | undefined;

  return () => {
    if (!client) {
      try {
        const wsLink = new GraphQLWsLink(
          createClient({
            url: 'wss://smart-search.sembo.com/graphql',
            shouldRetry: () => true,
          })
        );

        const errorLink = new ErrorLink(({ error }) => {
          if (CombinedGraphQLErrors.is(error)) {
            error.errors.forEach(({ message, locations, path }) => {
              const stringifiedLocations = locations
                ? JSON.stringify(locations)
                : 'No locations';

              throw new Error(
                `[GraphQL Error]: ${message}, Location: ${stringifiedLocations}, Path: ${path?.toString() ?? 'No path'}`
              );
            });
          } else if (error) {
            if (error.message.includes('Socket')) {
              throw new Error(
                `[GraphQL Error]: WebSocket error: ${error.message}`
              );
            } else {
              throw new Error(`[GraphQL Error]: ${error.message}`);
            }
          }
        });

        const retryLink = new RetryLink({
          attempts: (count): boolean => {
            const MAX_RETRIES = 3;

            if (count === 1) {
              trackGraphQLRetriesStarted();
            }

            if (count === MAX_RETRIES) {
              trackGraphQLRetriesFailed();
            }

            return count < MAX_RETRIES;
          },
          delay: { initial: 1000, jitter: false },
        });

        const link = ApolloLink.from([errorLink, retryLink, wsLink]);

        client = new ApolloClient({
          link,

          cache: new InMemoryCache({
            typePolicies: {
              ItineraryResponseQL: {
                keyFields: ['itineraryKey', 'prices'],
              },
              TimeSlotsQL: {
                keyFields: ['timeSlotId', 'startTime'],
              },
              AgeRangeQL: {
                keyFields: ['ageRangeId', 'price', 'quantityAvailable'],
              },
              SegmentQL: {
                keyFields: [
                  'flightNumber',
                  'departureDateTime',
                  'arrivalDateTime',
                ],
              },
            },
          }),

          devtools: {
            enabled: true,
          },
        });
      } catch {
        throw new Error('[GraphQL Error]: Failed to initialize Apollo Client.');
      }
    }

    return client;
  };
}

export const getApolloClient = apolloClientFactory();
